import React from 'react';
import '../../src/styles/terms.css';

const PrivacyPage: React.FC = () => {
  return (
    <div className="terms-root">
      <header className="terms-header">
        <div className="terms-brand"><a className="brand-link" href="/">Burst</a></div>
      </header>

      <main className="terms-main">
        <section className="terms-card" aria-labelledby="privacy-heading">
          <h1 id="privacy-heading">Privacy Policy</h1>
          <p className="terms-updated">Last updated: 8th October 2025</p>

          <p className="terms-intro">
            Burst Athletics (“<strong>Burst</strong>”, “we”, “us”) provides training programmes and related
            services via our website and mobile app (the “Services”). This policy explains what we collect,
            why we collect it and your choices.
          </p>
          <p className="terms-intro">
            Questions? Contact <a href="mailto:james@burstathletics.co.uk">james@burstathletics.co.uk</a>.
          </p>

          <ol className="terms-list">
            <li id="p1">
              <h2>Who controls your data</h2>
              <p><strong>Controller:</strong> Burst Athletics Ltd (UK)</p>
              <p><strong>Contact:</strong> <a href="mailto:james@burstathletics.co.uk">james@burstathletics.co.uk</a></p>
              {/* Optional company address line can be added here if you want it public */}
            </li>

            <li id="p2">
              <h2>What we collect</h2>
              <p><strong>From you</strong></p>
              <ul>
                <li>Account details (name, email, password—stored hashed).</li>
                <li>Profile &amp; preferences (training goals, equipment, date/time selections).</li>
                <li>Activity data (workouts created/completed, sets/reps, personal bests, notes).</li>
                <li>Communications (support messages, feedback, survey responses).</li>
              </ul>
              <p><strong>Automatically</strong></p>
              <ul>
                <li>Device &amp; usage data (app version, device/OS, time zone, crash logs, performance metrics).</li>
                <li>Analytics events (screens viewed and actions—aggregated/pseudonymised where possible).</li>
                <li>Subscription status (product, renewal state, country). We don’t store full card details.</li>
              </ul>
              <p><strong>From third parties</strong></p>
              <ul>
                <li>App stores and our entitlement/billing provider (e.g., RevenueCat) for subscription validation.</li>
                <li>Single sign-on providers if you choose to use them (if enabled in future).</li>
              </ul>
              <p>Payment card data is processed by Apple/Google; we do not collect or store full card numbers.</p>
            </li>

            <li id="p3">
              <h2>How we use your data (and legal bases)</h2>
              <ul>
                <li>Provide and improve the Services (create programmes, sync progress, fix bugs) — <em>contract, legitimate interests</em>.</li>
                <li>Personalise training (time-boxed sessions, equipment filters, recommendations) — <em>contract, legitimate interests</em>.</li>
                <li>Analytics &amp; research (feature performance, adoption) — <em>legitimate interests; consent where required</em>.</li>
                <li>Customer support and essential communications — <em>contract, legitimate interests</em>.</li>
                <li>Marketing with your opt-in where required — <em>consent</em> (you can opt out anytime).</li>
                <li>Security, fraud prevention and legal compliance — <em>legitimate interests, legal obligation</em>.</li>
              </ul>
            </li>

            <li id="p4">
              <h2>Sharing</h2>
              <p>We share data only with:</p>
              <ul>
                <li><strong>Service providers</strong> (hosting, analytics, crash reporting, email, entitlement/billing) acting under our instructions.</li>
                <li><strong>App stores/payment platforms</strong> (Apple App Store, Google Play) to manage subscriptions.</li>
                <li><strong>Authorities</strong> where required by law or to protect rights and safety.</li>
                <li><strong>Business transfers</strong> (e.g., merger or acquisition); we’ll notify you where required.</li>
              </ul>
              <p>We do <strong>not</strong> sell your personal information.</p>
            </li>

            <li id="p5">
              <h2>International transfers</h2>
              <p>
                We may transfer data outside the UK/EEA. When we do, we use appropriate safeguards (such as
                Standard Contractual Clauses) and require vendors to protect your data to GDPR standards.
              </p>
            </li>

            <li id="p6">
              <h2>Retention</h2>
              <ul>
                <li>Account &amp; workout data: kept while you have an account.</li>
                <li>If you delete your account, we erase or irreversibly anonymise personal data within a reasonable period, unless we must keep some for legal, tax or security reasons.</li>
                <li>Logs/analytics: kept for shorter rolling periods needed for troubleshooting and product improvement.</li>
              </ul>
            </li>

            <li id="p7">
              <h2>Your rights</h2>
              <p>Depending on where you live, you can:</p>
              <ul>
                <li>Access, correct or delete your data.</li>
                <li>Object to or restrict certain processing.</li>
                <li>Port your data to another service.</li>
                <li>Withdraw consent (e.g., marketing) at any time.</li>
                <li>Lodge a complaint with your supervisory authority (e.g., the ICO in the UK).</li>
              </ul>
              <p>
                To exercise rights, contact <a href="mailto:privacy@burst-athletics.com">privacy@burst-athletics.com</a>.
                We may need to verify your identity.
              </p>
              <p><strong>California residents:</strong> you have rights to know, delete and correct certain data. We do not sell or share your data for cross-context behavioural advertising.</p>
            </li>

            <li id="p8">
              <h2>Children</h2>
              <p>
                Our Services are not directed to children under 13 (or under 16 in the EU/UK). If you believe a child
                has provided personal data, contact us and we’ll remove it.
              </p>
            </li>

            <li id="p9">
              <h2>Security</h2>
              <p>
                We use technical and organisational measures to protect your data (encryption in transit, access controls,
                least-privilege practices). No system is 100% secure; we monitor and improve safeguards continuously.
              </p>
            </li>

            <li id="p10">
              <h2>Cookies &amp; similar technologies (website)</h2>
              <p>
                Our website uses cookies and similar technologies for functionality and analytics. Manage preferences via
                your browser settings or our cookie banner (where shown). See our Cookie Notice for details.
              </p>
            </li>

            <li id="p11">
              <h2>Changes to this policy</h2>
              <p>
                We may update this policy. We’ll post the new version here and change the “Last updated” date. If changes
                are significant, we’ll notify you in-app or by email. Continuing to use the Services means you accept the update.
              </p>
            </li>

            <li id="p12">
              <h2>Contact</h2>
              <p><strong>Burst Athletics Ltd</strong></p>
              <p>Email: <a href="mailto:james@burstathletics.co.uk">james@burstathletics.co.uk</a></p>
            </li>
          </ol>
        </section>
      </main>

      <footer className="terms-footer">
        <div className="terms-copy">© {new Date().getFullYear()} Burst Athletics</div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
